/**
 * @fileoverview `<RateLimitNotice>` — the ONE thing a throttled form shows when it refuses a submit.
 * @package @puffin-core/form-security
 *
 * UNREACHABLE-MODULE-BURNDOWN-001 Q-UMB-21 (2026-09-24). Measured before this existed: of pos-web's
 * sixteen `useRateLimit` forms, twelve `return`ed from the submit handler when the limit was hit —
 * the user clicked and NOTHING happened, with no message and an enabled button — two set a generic
 * error sentence with no countdown, and two carried their own byte-identical `RateLimitWarning`.
 * prep-web's public demo request and purchase-web's four throttled forms refused the same way. A
 * refusal the user cannot see is indistinguishable from a broken form.
 *
 * So every consumer renders this beside its form AND disables its submit on `isLimited`; a
 * cross-app contract (`rateLimitRefusalsAreVisible`) holds that pairing for every consumer file.
 *
 * ## Two sentences, two live-region roles — and why they are not one element
 *
 * Both notices are the ONE feedback component (SAVE-FEEDBACK-CANON-001 P3). The lock notice is
 * `<InlineFeedback tone="error" announce="title">`:
 *
 * - The refusal is the component's TITLE and its live region, `role="alert"`: announced ONCE, the
 *   moment the lock starts, because its text never changes while it is on screen. A refused submit
 *   is an error, and the one rule couples the tone with the role.
 * - The countdown is the block body, `role="timer"`, whose implicit `aria-live` is `off`, OUTSIDE
 *   the title and under no live root (`announce="title"` keeps the root role-less). It re-renders
 *   every second; inside a live region it would be re-announced every second for up to fifteen
 *   minutes. The pre-consolidation login notice did exactly that — one `role="status"` element
 *   carrying the ticking number.
 *
 * The countdown is a CLOCK (`0:45`, `14:59`), not "N seconds" — `formatCountdown`, its own DOM-free module
 * since LIFE-SHORTLISTED-001 D13, so palm-native's native notice reads the same clock.
 *
 * @module @puffin-core/form-security/RateLimitNotice
 */

import PropTypes from 'prop-types';
import { InlineFeedback } from '@puffin-core/feedback-ui';

import { formatCountdown } from './formatCountdown.js';
import { useFormSecurityTranslate } from './useFormSecurityTranslate.js';

/**
 * Render the lock notice while `rateLimit.isLimited`; otherwise, when `showRemainingAttempts` is
 * set and at least one attempt has been counted, the attempts-left warning; otherwise nothing.
 *
 * @param {object} props
 * @param {object} props.rateLimit - the `useRateLimit()` return value, passed whole
 * @param {boolean} [props.showRemainingAttempts=false] - warn how many attempts are left before the
 *   lock. For a surface that counts FAILURES (a sign-in); on a surface where every submission counts,
 *   a warning after the first ordinary send is noise.
 * @param {Function} [props.t] - optional consumer translator, consulted before the namespace
 * @param {string} [props.message] - the refusal sentence to say INSTEAD of the generic locked one,
 *   when the lock answers a SERVER refusal whose translated outcome is more specific (a contact form's
 *   per-email limiter names the address). The countdown is unchanged. A form that renders this notice
 *   and its own error banner for the same refusal says it twice — two `role="alert"` elements — so the
 *   consumer hands the sentence here and renders no banner while locked.
 * @returns {React.JSX.Element|null}
 */
export function RateLimitNotice({ rateLimit, showRemainingAttempts = false, t, message }) {
  const translate = useFormSecurityTranslate(t);

  if (rateLimit.isLimited) {
    return (
      <InlineFeedback
        tone="error"
        announce="title"
        title={message || translate('formSecurity.rateLimit.locked')}
        className="mb-4"
        testId="rate-limit-notice"
      >
        <p className="alert-message" role="timer" data-testid="rate-limit-countdown">
          {translate('formSecurity.rateLimit.countdown', {
            time: formatCountdown(rateLimit.timeUntilReset),
          })}
        </p>
      </InlineFeedback>
    );
  }

  if (showRemainingAttempts && rateLimit.attemptCount > 0) {
    return (
      <InlineFeedback
        tone="warning"
        message={translate('formSecurity.rateLimit.remaining', {
          attempts: String(rateLimit.remainingAttempts),
        })}
        className="mb-4"
        testId="rate-limit-remaining"
      />
    );
  }

  return null;
}

RateLimitNotice.propTypes = {
  rateLimit: PropTypes.shape({
    isLimited: PropTypes.bool.isRequired,
    attemptCount: PropTypes.number.isRequired,
    remainingAttempts: PropTypes.number.isRequired,
    timeUntilReset: PropTypes.number.isRequired,
  }).isRequired,
  showRemainingAttempts: PropTypes.bool,
  t: PropTypes.func,
  message: PropTypes.string,
};

export default RateLimitNotice;
